// Module 3.2b — Drill String schema (v1).
//
// A DrillString record represents the tubular section ABOVE the BHA: drill
// pipe, HWDP and any in-string crossovers. The BHA hangs on the bottom and
// stays in its own module (3.2a). Total tally = DrillString + BHA.
//
// Convention (matches BHA): `sections[0]` is the section immediately ABOVE
// the BHA top (closest to the bit), sections[N-1] is the highest section
// near the rig floor. Walking the array from index 0 upward mirrors the
// BHA's bit-outward walk so a future "full string" view can concatenate
// BHA.components ++ DrillString.sections without re-ordering.
//
// Permanent sectionUid survives reorders / removals so future modules
// (inspection / inventory / dent-tally) can track a single section across
// many drill strings.
//
// Auto-sequencing: `sequence` is overwritten by storage.js on every persist.

export const DRILLSTRING_SCHEMA_VERSION = 1;

// Internal capacity constant — (ID²) / 1029.4 = bbl/ft, the standard
// oilfield identity. Keeping it here (not in lib/openg/units) because it is
// a geometric tubular property, not a kill-sheet engine constant.
export const BBL_PER_FT_DIVISOR = 1029.4;

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------
export const DS_ASSEMBLY_STATUSES   = ["draft", "approved", "locked"];
export const DS_OPERATIONAL_STATUSES = ["planned", "in-hole", "racked", "retired"];

// Section roles + which Module 2 reference library category each maps to.
export const DRILLSTRING_SECTION_ROLES = [
  { role: "drill_pipe", label: "Drill Pipe", refCategory: "drill_pipe" },
  { role: "hwdp",       label: "HWDP",       refCategory: "hwdp"       },
  { role: "crossover",  label: "Crossover",  refCategory: "crossover"  },
];
export const VALID_DS_ROLES = DRILLSTRING_SECTION_ROLES.map((r) => r.role);
export function dsRoleLabel(role) {
  const r = DRILLSTRING_SECTION_ROLES.find((x) => x.role === role);
  return r ? r.label : role;
}
export function dsRoleRefCategory(role) {
  const r = DRILLSTRING_SECTION_ROLES.find((x) => x.role === role);
  return r ? r.refCategory : "";
}

// ---------------------------------------------------------------------------
// Factories
// ---------------------------------------------------------------------------
let _uidCounter = 0;
function _newSectionUid() {
  _uidCounter += 1;
  return `dss_${Date.now()}_${_uidCounter}_${Math.random().toString(36).slice(2, 8)}`;
}

export function emptyDrillStringSection(role = "drill_pipe") {
  return {
    sectionUid: _newSectionUid(),          // permanent
    sequence: 0,                            // auto-rebaked by storage
    role,
    refCategory: dsRoleRefCategory(role),
    refId: "",
    displayName: "",
    outerDiameter: "",                      // in
    innerDiameter: "",                      // in
    weightPpf: "",                          // lb/ft
    grade: "",
    connection: "",
    // Length sources — either explicit lengthFt OR jointCount × nominalJointLengthFt.
    // When both are set, lengthFt wins (treat as user override).
    lengthFt: "",
    jointCount: "",
    nominalJointLengthFt: "",               // e.g. 31.5 (Range II avg)
    notes: "",
  };
}

export function emptyDrillStringRecord() {
  return {
    id: "",
    schemaVersion: DRILLSTRING_SCHEMA_VERSION,
    wellId: "",

    // Identity
    name: "",
    stringNumber: "",
    runNumber: "",
    revision: "",                           // optional — "A", "B", "C" …

    // Lineage — the BHA this drill string hangs on (optional, picker on the
    // editor). Stored as ID only; the BHA module is the source of truth.
    bhaId: "",

    // Two-track status (mirrors BHA)
    assemblyStatus: "draft",
    operationalStatus: "planned",

    // Operating window (mirrors BHA — populate at instructor's discretion)
    startDateTime: "",
    endDateTime: "",
    depthInFt: "",
    depthOutFt: "",

    // Sections — ordered from bit-end outward (sections[0] sits on top of
    // the BHA; sections[N-1] is the topmost section near the rig floor).
    sections: [],

    notes: "",
    isActive: true,
    createdAt: null,
    updatedAt: null,
  };
}

// ---------------------------------------------------------------------------
// Per-section derived geometry — PURE.
//
//   effectiveLengthFt    = lengthFt if set, else jointCount × nominalJointLengthFt
//   internalCapacityBblFt = ID² / 1029.4
//   internalVolumeBbl    = effectiveLengthFt × internalCapacityBblFt
//   weightLbs            = effectiveLengthFt × weightPpf
//
// Returns "" (so the UI renders "—") if any required input is missing or
// non-finite. Never throws.
// ---------------------------------------------------------------------------
const _num = (v) =>
  v !== "" && v != null && Number.isFinite(Number(v)) ? Number(v) : null;

export function computeSectionGeometry(section) {
  if (!section) return null;
  const len   = _num(section.lengthFt);
  const jc    = _num(section.jointCount);
  const nlen  = _num(section.nominalJointLengthFt);
  const id    = _num(section.innerDiameter);
  const wppf  = _num(section.weightPpf);

  const effLen = len != null ? len : (jc != null && nlen != null ? jc * nlen : null);
  const capBblFt = (id != null && id > 0) ? (id * id) / BBL_PER_FT_DIVISOR : null;
  const intVol = (effLen != null && capBblFt != null) ? effLen * capBblFt : null;
  const wtLbs  = (effLen != null && wppf != null)     ? effLen * wppf     : null;

  return {
    effectiveLengthFt:        effLen ?? "",
    internalCapacityBblPerFt: capBblFt ?? "",
    internalVolumeBbl:        intVol ?? "",
    weightLbs:                wtLbs ?? "",
    effectiveJointCount:      jc ?? "",
  };
}

// ---------------------------------------------------------------------------
// Whole-record geometry + Live Summary Totals.
//
// Walks every section, sums lengths / weights / internal volumes / joint
// counts and stamps cumulative running depths (top + bottom of each
// section measured from the BHA top going UP — i.e. the section nearest
// to BHA is at lowest depth coordinate 0..L_first).
//
// This is the function the UI's Live Summary Totals strip + the
// re-pull into the Kill Sheet's stringInternalVolBbl both consume.
// Returns a NEW object — never mutates the input.
// ---------------------------------------------------------------------------
export function computeDrillStringGeometry(rec) {
  if (!rec || !Array.isArray(rec.sections)) {
    return {
      ...rec,
      sections: [],
      summary: _emptySummary(),
    };
  }
  let runningLen = 0;
  let bailed = false;

  let totalLength = 0;
  let totalWeight = 0;
  let totalIntVol = 0;
  let totalJoints = 0;
  // Track whether every section contributed numerically; if any was
  // missing data the headline totals can't be trusted as completes.
  let allLengthsKnown  = rec.sections.length > 0;
  let allInternalKnown = rec.sections.length > 0;
  let allWeightsKnown  = rec.sections.length > 0;
  let anyJointCount    = false;

  // `Number("")` evaluates to 0 (finite!), so cell-level numerics must
  // reject empty strings explicitly before calling Number().
  const _isCellFinite = (v) =>
    v !== "" && v != null && Number.isFinite(Number(v));

  const sections = rec.sections.map((s) => {
    const g = computeSectionGeometry(s) || {};
    const out = { ...s, ...g };

    if (!bailed && _isCellFinite(g.effectiveLengthFt)) {
      const L = Number(g.effectiveLengthFt);
      out.distanceFromBhaTopFt   = runningLen;
      runningLen += L;
      out.cumulativeLengthFt     = runningLen;
      totalLength += L;
    } else {
      out.distanceFromBhaTopFt = "";
      out.cumulativeLengthFt   = "";
      bailed = true;
      allLengthsKnown = false;
    }

    if (_isCellFinite(g.internalVolumeBbl)) totalIntVol += Number(g.internalVolumeBbl);
    else allInternalKnown = false;

    if (_isCellFinite(g.weightLbs)) totalWeight += Number(g.weightLbs);
    else allWeightsKnown = false;

    if (_isCellFinite(g.effectiveJointCount)) {
      totalJoints += Number(g.effectiveJointCount);
      anyJointCount = true;
    }
    return out;
  });

  return {
    ...rec,
    sections,
    summary: {
      sectionCount:               sections.length,
      totalLengthFt:              allLengthsKnown  ? totalLength : "",
      totalWeightLbs:             allWeightsKnown  ? totalWeight : "",
      totalInternalVolumeBbl:     allInternalKnown ? totalIntVol : "",
      totalJointCount:            anyJointCount    ? totalJoints : "",
    },
  };
}

function _emptySummary() {
  return {
    sectionCount: 0,
    totalLengthFt: "",
    totalWeightLbs: "",
    totalInternalVolumeBbl: "",
    totalJointCount: "",
  };
}

// ---------------------------------------------------------------------------
// Auto-sequencing (mirrors BHA)
// ---------------------------------------------------------------------------
export function resequenceSections(rec) {
  if (!rec || !Array.isArray(rec.sections)) return rec;
  const sections = rec.sections.map((s, i) => ({ ...s, sequence: i }));
  return { ...rec, sections };
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------
const _isPosNum    = (v) => v !== "" && v != null && Number.isFinite(Number(v)) && Number(v) > 0;
const _isNonNegNum = (v) => v !== "" && v != null && Number.isFinite(Number(v)) && Number(v) >= 0;
const _optPosNum    = (v) => v === "" || v == null || _isPosNum(v);
const _optNonNegNum = (v) => v === "" || v == null || _isNonNegNum(v);

function _validateSection(s, idx) {
  const errs = [];
  const tag = `Section #${idx + 1} (${dsRoleLabel(s.role)})`;
  if (!VALID_DS_ROLES.includes(s.role))
    errs.push(`${tag}: unknown role "${s.role}".`);
  if (!_optPosNum(s.outerDiameter)) errs.push(`${tag}: OD must be > 0 if entered.`);
  if (!_optPosNum(s.innerDiameter)) errs.push(`${tag}: ID must be > 0 if entered.`);
  if (_isPosNum(s.outerDiameter) && _isPosNum(s.innerDiameter) &&
      Number(s.outerDiameter) <= Number(s.innerDiameter))
    errs.push(`${tag}: OD must be greater than ID.`);
  if (!_optPosNum(s.weightPpf)) errs.push(`${tag}: weight must be > 0 if entered.`);
  if (!_optPosNum(s.lengthFt))  errs.push(`${tag}: length must be > 0 if entered.`);
  if (s.jointCount !== "" && s.jointCount != null) {
    const n = Number(s.jointCount);
    if (!Number.isInteger(n) || n <= 0)
      errs.push(`${tag}: joint count must be a positive integer.`);
  }
  if (!_optPosNum(s.nominalJointLengthFt))
    errs.push(`${tag}: nominal joint length must be > 0 if entered.`);
  // Either explicit length OR (jointCount + nominalJointLength). If none of
  // these are present, length is unknown — warn at validation time.
  if (!_isPosNum(s.lengthFt) &&
      !(_isPosNum(s.jointCount) && _isPosNum(s.nominalJointLengthFt))) {
    errs.push(`${tag}: enter either a length OR a joint count + nominal joint length.`);
  }
  if (!s.sectionUid || !String(s.sectionUid).trim())
    errs.push(`${tag}: missing permanent sectionUid (internal error — re-create the row).`);
  return errs;
}

export function validateDrillStringRecord(rec) {
  if (!rec || typeof rec !== "object")
    return { ok: false, errors: ["Drill string record is missing."] };

  const errs = [];

  if (!rec.wellId || !String(rec.wellId).trim())
    errs.push("Drill string must be linked to a well.");
  if (!rec.name || !String(rec.name).trim())
    errs.push("Drill string name is required.");

  if (!DS_ASSEMBLY_STATUSES.includes(rec.assemblyStatus))
    errs.push(`Assembly status must be one of: ${DS_ASSEMBLY_STATUSES.join(", ")}.`);
  if (!DS_OPERATIONAL_STATUSES.includes(rec.operationalStatus))
    errs.push(`Operational status must be one of: ${DS_OPERATIONAL_STATUSES.join(", ")}.`);

  for (const [f, label] of [
    ["depthInFt", "Depth in"], ["depthOutFt", "Depth out"],
  ]) {
    if (!_optNonNegNum(rec[f])) errs.push(`${label} must be ≥ 0 if entered.`);
  }
  if (_isNonNegNum(rec.depthInFt) && _isNonNegNum(rec.depthOutFt) &&
      Number(rec.depthInFt) > Number(rec.depthOutFt))
    errs.push("Depth in cannot exceed depth out.");
  if (rec.startDateTime && rec.endDateTime &&
      String(rec.startDateTime) > String(rec.endDateTime))
    errs.push("Start date must be on or before end date.");

  const sections = Array.isArray(rec.sections) ? rec.sections : [];
  if (sections.length === 0) errs.push("Drill string must contain at least one section.");
  sections.forEach((s, i) => { errs.push(..._validateSection(s, i)); });

  // sectionUid uniqueness within the record
  const uids = new Set();
  for (const s of sections) {
    if (s.sectionUid && uids.has(s.sectionUid))
      errs.push(`Duplicate sectionUid "${s.sectionUid}" — internal sequencing error.`);
    if (s.sectionUid) uids.add(s.sectionUid);
  }

  return { ok: errs.length === 0, errors: errs };
}

// ---------------------------------------------------------------------------
// Summary projection — cached on WellProfile.children.drillStrings[].
// ---------------------------------------------------------------------------
export function summariseDrillString(rec) {
  if (!rec) return null;
  const computed = computeDrillStringGeometry(rec);
  return {
    id: rec.id,
    name: rec.name,
    stringNumber: rec.stringNumber || "",
    revision: rec.revision || "",
    bhaId: rec.bhaId || "",
    assemblyStatus: rec.assemblyStatus,
    operationalStatus: rec.operationalStatus,
    sectionCount: computed.summary.sectionCount,
    totalLengthFt: computed.summary.totalLengthFt,
    totalInternalVolumeBbl: computed.summary.totalInternalVolumeBbl,
    updatedAt: rec.updatedAt,
  };
}
